/**
 * CardHeader component - Displays date range and status label
 */

import React from 'react'
import { Typography, Tag } from 'antd'
import type { TagProps } from 'antd'
import { format } from 'date-fns'
import { useIntl } from 'react-intl'
import { getDateFnsLocale } from '@vacationtracker/shared/i18n'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { normalizeISODateString } from '../utils/dateUtils'
import { StatusLabelType } from '../utils/statusLabel'
import { useAppSelector } from '../../../../../store/hooks'
import { selectLocaleSlice } from '../../../../../store/locale-slice'

const { Title } = Typography

interface CardHeaderProps {
  startDate: string
  endDate: string
  statusLabelType: StatusLabelType
  isBestOption: boolean
}

/**
 * Formats a date range for display
 * Handles UTC ISO dates correctly by extracting the date part before parsing
 *
 * @example
 * formatDateRange('2026-01-01T00:00:00.000Z', '2026-01-05T00:00:00.000Z', 'en') // "Wed, Jan 1 – Sun, Jan 5"
 */
function formatDateRange(startDate: string, endDate: string, locale: LocaleEnum): string {
  // Extract date part from ISO strings to avoid timezone conversion issues
  const startDateStr = normalizeISODateString(startDate)
  const endDateStr = normalizeISODateString(endDate)

  // Parse date components (YYYY-MM-DD)
  const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number)
  const [endYear, endMonth, endDay] = endDateStr.split('-').map(Number)

  // Create local Date objects using the calendar date (not UTC conversion)
  // This ensures the displayed date matches the calendar date from the API
  const start = new Date(startYear, startMonth - 1, startDay)
  const end = new Date(endYear, endMonth - 1, endDay)

  const dateFnsLocale = getDateFnsLocale(locale)
  const startFormatted = format(start, 'EEE, MMM d', { locale: dateFnsLocale })
  const endFormatted = format(end, 'EEE, MMM d', { locale: dateFnsLocale })

  return `${startFormatted} – ${endFormatted}`
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  startDate,
  endDate,
  statusLabelType,
  isBestOption,
}) => {
  const { formatMessage } = useIntl()
  const { locale } = useAppSelector(selectLocaleSlice)

  // Get the localized status label text
  const getStatusLabelText = (type: StatusLabelType): string => {
    switch (type) {
      case StatusLabelType.BEST_OPTION:
        return formatMessage({ id: 'energyLevel.bestOption' })
      case StatusLabelType.QUICK_WIN:
        return formatMessage({ id: 'energyLevel.quickWin' })
      case StatusLabelType.GOOD_OPTION:
        return formatMessage({ id: 'energyLevel.goodOption' })
      case StatusLabelType.DEEP_RECOVERY:
        return formatMessage({ id: 'energyLevel.deepRecovery' })
      default:
        return formatMessage({ id: 'energyLevel.goodOption' })
    }
  }

  // Map status type to Ant Design Tag color
  const getStatusColor = (type: StatusLabelType): string => {
    if (isBestOption) {
      return 'success'
    }
    switch (type) {
      case StatusLabelType.QUICK_WIN:
        return 'warning'
      case StatusLabelType.GOOD_OPTION:
        return 'processing'
      case StatusLabelType.DEEP_RECOVERY:
        return 'success'
      default:
        return 'default'
    }
  }

  const statusLabel = getStatusLabelText(statusLabelType)
  const statusColor = getStatusColor(statusLabelType)

  return (
    <div className="suggested-action-card-header">
      <Title level={5} className="date-range-title">
        {formatDateRange(startDate, endDate, locale.locale)}
      </Title>
      <Tag
        className="status-label-tag"
        {...({ variant: 'outlined', color: statusColor } as TagProps)}
      >
        {statusLabel}
      </Tag>
    </div>
  )
}
